// RolePermissionTree.jsx
import { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useUserMenus } from '../../context/useUserMenus';
import { toast } from 'react-toastify';

export default function RolePermissionTree() {
  const [roles, setRoles] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [indeterminate, setIndeterminate] = useState({});
  const { user } = useAuth();
  const { refetch } = useUserMenus(user?.id);

  useEffect(() => {
    axios.get('/roles').then(res => setRoles(res.data));
  }, []);

  useEffect(() => {
    if (selectedRole) {
      axios.get(`/roles/role-pages/detail/${selectedRole}`).then(res => {
        setPages(res.data);
        const assigned = res.data.filter(p => p.assigned).map(p => p.id);
        setSelectedPages(assigned);
        updateIndeterminateState(res.data, assigned);
      });
    }
  }, [selectedRole]);

  const getChildPages = (id) => pages.filter(p => p.parent_id === id);

  const updateIndeterminateState = (allPages, selected) => {
    const state = {};
    allPages.forEach(page => {
      const children = getChildPages(page.id).map(c => c.id);
      if (children.length > 0) {
        const intersect = children.filter(id => selected.includes(id));
        if (intersect.length > 0 && intersect.length < children.length) {
          state[page.id] = true;
        }
      }
    });
    setIndeterminate(state);
  };

  const togglePage = (id, children = []) => {
    let updated = [...selectedPages];
    const isSelected = updated.includes(id);
    if (isSelected) {
      updated = updated.filter(i => i !== id && !children.includes(i));
    } else {
      updated = [...new Set([...updated, id, ...children])];
    }
    setSelectedPages(updated);
    updateIndeterminateState(pages, updated);
  };

  const selectAll = () => {
    const allIds = pages.map(p => p.id);
    setSelectedPages(allIds);
    updateIndeterminateState(pages, allIds);
  };

  const deselectAll = () => {
    setSelectedPages([]);
    updateIndeterminateState(pages, []);
  };

  const renderTree = (parentId = null) => {
    return pages
      .filter(p => p.parent_id === parentId)
      .map(page => {
        const children = getChildPages(page.id);
        const isChecked = selectedPages.includes(page.id);
        const isIndeterminate = indeterminate[page.id];

        return (
          <div key={page.id} className="ml-6 my-1">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                className="form-checkbox h-4 w-4 text-blue-600"
                checked={isChecked}
                ref={el => { if (el) el.indeterminate = isIndeterminate; }}
                onChange={() => togglePage(page.id, children.map(c => c.id))}
              />
              <span className="text-gray-700 dark:text-gray-200">{page.label}</span>
            </label>
            {renderTree(page.id)}
          </div>
        );
      });
  };

  const savePermissions = async () => {
    await axios.post(`/roles/role-pages/${selectedRole}`, { pageIds: selectedPages }).then((res)=>{
      toast.success("Permission Update Successfull")
    });
    if (user.role === Number(selectedRole)) {
      await refetch(); // refresh menus if current user role was updated
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto bg-gray-100 dark:bg-gray-900 rounded-lg shadow">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Role Page Permissions</h2>

      <div className="flex items-center gap-4 mb-4">
        <select
          className="border rounded px-4 py-2 text-gray-800 dark:text-white bg-white dark:bg-gray-800"
          onChange={e => setSelectedRole(e.target.value)}
          value={selectedRole || ''}
        >
          <option value=''>Select Role</option>
          {roles.map(role => (
            <option key={role.id} value={role.id}>{role.name}</option>
          ))}
        </select>

        <button onClick={selectAll} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm">Select All</button>
        <button onClick={deselectAll} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">Deselect All</button>
      </div>

      {selectedRole && (
        <>
          <div className="border p-4 rounded bg-white dark:bg-gray-800 shadow">
            {renderTree()}
          </div>

          <div className="mt-6 text-right">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded" onClick={savePermissions}>
              Save Access
            </button>
          </div>
        </>
      )}
    </div>
  );
}
